import React, { useState, useMemo, useCallback } from "react";
import { Layout, Button, Space, Drawer, Grid } from "antd";
import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../hooks/useAuth";
import { useBreadcrumbs } from "../../../hooks/useBreadcrumbs";
import ThemeToggle from "../../common/ThemeToggle";
import AppBreadcrumb from "../../common/AppBreadcrumb";
import SystemTopAlerts from "../../common/SystemTopAlerts";
import { useTheme } from "../../../hooks/useTheme";
import DashboardSidebar from "./DashboardSidebar";

const { Header, Content, Sider } = Layout;
const { useBreakpoint } = Grid;

/**
 * Layout principal da aplicação.
 *
 * Mantido montado via Router (PrivateLayout) para evitar remontagem do menu
 * a cada navegação entre páginas.
 */
const DashboardLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { logout } = useAuth();
  const { theme: currentTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const breadcrumbs = useBreadcrumbs();
  const isDark = currentTheme === 'dark';
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const selectedKey = location.pathname;

  const handleLogout = useCallback(() => {
    logout();
    navigate("/login");
  }, [logout, navigate]);

  const handleCloseDrawer = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  const sidebarProps = useMemo(() => ({
    theme: currentTheme,
    isDark,
    selectedKey,
    onLogout: handleLogout,
  }), [currentTheme, isDark, selectedKey, handleLogout]);

  return (
    <Layout hasSider={!isMobile} style={{ minHeight: "100vh" }}>
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          width={250}
          collapsedWidth={80}
          theme={currentTheme}
          style={{
            overflow: "visible",
            height: "100vh",
            position: "fixed",
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 100,
            backgroundColor: isDark ? "#141414" : "#ffffff",
            borderRight: isDark ? "none" : "1px solid #f0f0f0",
          }}
        >
          <DashboardSidebar
            {...sidebarProps}
            isDrawer={false}
            collapsed={collapsed}
          />
        </Sider>
      )}

      {isMobile && (
        <Drawer
          open={mobileMenuOpen}
          onClose={handleCloseDrawer}
          placement="left"
          width={260}
          styles={{
            wrapper: { backgroundColor: isDark ? "#141414" : "#ffffff" },
            content: { backgroundColor: isDark ? "#141414" : "#ffffff", boxShadow: "none" },
            body: { padding: 0, backgroundColor: isDark ? "#141414" : "#ffffff" },
            header: { display: "none" },
          }}
        >
          <DashboardSidebar
            {...sidebarProps}
            isDrawer
            collapsed={false}
            onCloseDrawer={handleCloseDrawer}
          />
        </Drawer>
      )}

      <Layout
        style={{
          marginLeft: isMobile ? 0 : collapsed ? 80 : 250,
          transition: "margin-left 0.2s",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Header
          style={{
            padding: "0 12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: 64,
            lineHeight: "64px",
          }}
        >
          <Space align="center" size="middle">
            <Button
              type="text"
              icon={
                isMobile || collapsed
                  ? <MenuUnfoldOutlined />
                  : <MenuFoldOutlined />
              }
              onClick={() => {
                if (isMobile) {
                  setMobileMenuOpen(true);
                } else {
                  setCollapsed((prev) => !prev);
                }
              }}
              style={{
                fontSize: "16px",
                width: 64,
                height: 64,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            />
            <AppBreadcrumb items={breadcrumbs} isMobile={isMobile} />
          </Space>

          <Space align="center" size="middle">
            <ThemeToggle />
          </Space>
        </Header>

        <SystemTopAlerts isMobile={isMobile} />

        <Content
          style={{
            flex: 1,
            padding: isMobile ? 8 : 16,
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

const Dashboard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <DashboardLayout>{children}</DashboardLayout>
);

export default Dashboard;
